import React, { useEffect, useState } from 'react';
import {
  Backdrop,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Rating,
} from '@mui/material';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';

import InputField from '../../common/fields/InputField';
import PrimaryButton from '../../common/buttons/PrimaryButton';
import BorderedButton from '../../common/buttons/BorderedButton';
import { submitReview } from '../../../services/teacher.service';
import { REVIEW_TIME_FRAME_OPTIONS } from '../../../constants/reviewConstants';

const initialForm = {
  rating: 0,
  time_frame: '',
  comment: '',
};

const initialErrors = {
  rating: '',
  time_frame: '',
  comment: '',
};

export default function ReviewModal({ open, teacherId, onClose, onSuccess }) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState(initialErrors);
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    setErrors(initialErrors);
    setSubmitError('');
  }, [open]);

  const validate = () => {
    const nextErrors = {
      rating: form.rating ? '' : 'Rating is required.',
      time_frame: form.time_frame ? '' : 'Time frame is required.',
      comment: form.comment.trim() ? '' : 'Comment is required.',
    };

    setErrors(nextErrors);
    return !Object.values(nextErrors).some(Boolean);
  };

  const handleClose = () => {
    if (loading) return;

    setForm(initialForm);
    setErrors(initialErrors);
    setSubmitError('');
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading || !validate()) return;

    setLoading(true);
    setSubmitError('');

    try {
      const res = await submitReview(teacherId, {
        rating: form.rating,
        time_frame: form.time_frame,
        comment: form.comment,
      });

      onSuccess?.(res);
      setForm(initialForm);
      setErrors(initialErrors);
      onClose?.();
    } catch (err) {
      const data = err.response?.data;

      if (err.response?.status === 422 && data?.errors) {
        setErrors((prev) => ({
          ...prev,
          rating: data.errors.rating?.[0] ?? prev.rating,
          time_frame: data.errors.time_frame?.[0] ?? prev.time_frame,
          comment: data.errors.comment?.[0] ?? prev.comment,
        }));
      }

      setSubmitError(data?.message ?? data?.error ?? 'Failed to submit review.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: 'blur(8px)',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
          },
          paper: {
            sx: {
              background: 'linear-gradient(158deg, rgba(17, 24, 39, 1) 0%, rgba(15, 15, 18, 1) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
            },
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          <div className="mb-5">
            <h3 className="text-xl font-semibold text-white">Submit Review</h3>
            <p className="mt-1 text-sm text-gray-400">Share your experience with this teacher.</p>
          </div>

          {submitError ? (
            <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3">
              <p className="text-sm text-red-400">{submitError}</p>
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <p className="mb-2 text-sm font-medium text-white">
                Rating <span className="text-red-400">*</span>
              </p>
              <Rating
                value={form.rating}
                onChange={(_, value) => {
                  setForm((prev) => ({ ...prev, rating: value ?? 0 }));
                  if (errors.rating) setErrors((prev) => ({ ...prev, rating: '' }));
                }}
                precision={1}
                icon={<StarRateRoundedIcon fontSize="inherit" />}
                emptyIcon={<StarBorderRoundedIcon fontSize="inherit" sx={{ color: '#F6911D' }} />}
                sx={{ color: '#F6911D' }}
              />
              {errors.rating ? <p className="mt-1 text-sm text-red-400">{errors.rating}</p> : null}
            </div>

            <FormControl fullWidth error={!!errors.time_frame}>
              <InputLabel sx={{ color: 'rgba(255,255,255,0.6)', '&.Mui-focused': { color: '#F6911D' } }}>
                Time Frame *
              </InputLabel>
              <Select
                value={form.time_frame}
                label="Time Frame *"
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, time_frame: e.target.value }));
                  if (errors.time_frame) setErrors((prev) => ({ ...prev, time_frame: '' }));
                }}
                sx={{
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#F6911D' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#F6911D' },
                }}
                >
                {REVIEW_TIME_FRAME_OPTIONS.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.time_frame ? <FormHelperText>{errors.time_frame}</FormHelperText> : null}
            </FormControl>

            <InputField
              label="Comment"
              value={form.comment}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, comment: e.target.value }));
                if (errors.comment) setErrors((prev) => ({ ...prev, comment: '' }));
              }}
              required
              multiline
              rows={4}
              error={!!errors.comment}
              helperText={errors.comment}
            />

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <BorderedButton onClick={handleClose} disabled={loading} sx={{ minWidth: 120 }}>
                Cancel
              </BorderedButton>
              <PrimaryButton type="submit" disabled={loading} sx={{ minWidth: 140 }}>
                {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Submit'}
              </PrimaryButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
